import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import useStyles from "./style";
import {
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
} from "@mui/material";
import {
  SearchOutlined,
  ChevronLeft,
  ChevronRight,
  ArrowBackOutlined,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as yup from "yup";
import _ from "lodash";
import moment from "moment-timezone";
import { CUSTOM_CONSTANTS } from "Constants";

import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";

// Services
import UserwalletApi from "Services/Wallet";

const Userwalletlogs = () => {
  const { id: walletid, user_id: userId } = useParams();
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [row, setRow] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [totalamount, setTotalamount] = useState(0);
  const MONEY_OPTIONS = [20, 40, 60];
  const [selectedAmount, setSelectedAmount] = useState(null);
  const timezone = useSelector((state) => state.timezone.timezone);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Get userWallet list api
  const getuserWalletlogs = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setTotalPages(res?.data?.data?.totalRecords || 1);
        setRow(res?.data?.data?.data || []);
        setTotalamount(res?.data?.data?.data?.[0]?.userwallet?.walletBalance || 0);
      } else {
        setRow([]);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    UserwalletApi.getusersWalletLogs({ page, limit: pageSize, walletID: walletid }).then(
      onSuccess,
      onFailure
    );
  }, [page, pageSize, walletid, dispatch]);

  useEffect(() => {
    getuserWalletlogs();
  }, [getuserWalletlogs]);

  const calculateSno = (pageIndex, pSize, index) => {
    return (pageIndex - 1) * pSize + index + 1;
  };

  const dataRows = useMemo(() => {
    let filtered = row || [];
    if (searchText.trim()) {
      const query = searchText.toLowerCase();
      filtered = filtered.filter(
        (data) =>
          data?.userdetails?.user_name?.toLowerCase().includes(query) ||
          data?.userdetails?.email?.toLowerCase().includes(query) ||
          data?.paymentUpdatedType?.toLowerCase().includes(query) ||
          data?.paymentStatus?.toLowerCase().includes(query)
      );
    }
    return _.map(filtered, (data, index) => ({
      sno: calculateSno(page, pageSize, index),
      username: data?.userdetails?.user_name || "-",
      email: data?.userdetails?.email || "-",
      mobile: `${data?.userdetails?.countryCode || ""} ${
        data?.userdetails?.mobileNumber || ""
      }`,
      mode: data?.paymentUpdatedType || "-",
      amount: data?.creditedAmount || 0,
      status: data?.paymentStatus || "-",
      createdAt: data?.createdAt
        ? moment(data?.createdAt).tz(timezone).format("YYYY-MM-DD hh:mm A")
        : "-",
      row: data,
    }));
  }, [row, page, pageSize, searchText, timezone]);

  const handlePresetAmountClick = (amount) => {
    formik.setFieldValue("creditedAmount", amount);
    setSelectedAmount(amount);
  };

  const handleUpdatewallet = (formValues) => {
    dispatch(showLoader("Loading please wait..."));

    const data = {
      creditedAmount: formValues?.creditedAmount,
      walletId: walletid,
      userId: userId,
    };

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: "Rewards Updated Successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        formik.resetForm();
        setSelectedAmount(null);
        getuserWalletlogs();
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Error while updating rewards",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };

    UserwalletApi.addwalletmoney(data).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: {
      creditedAmount: "",
    },
    validationSchema: yup.object({
      creditedAmount: yup.number().required("Amount is required").nullable(),
    }),
    enableReinitialize: true,
    onSubmit: handleUpdatewallet,
  });

  // Pagination
  const totalPagesCount = Math.ceil(totalPages / pageSize) || 1;

  const renderPagination = () => {
    let pages = [];
    const internalPage = page - 1;
    if (totalPagesCount <= 5) {
      for (let i = 0; i < totalPagesCount; i++) pages.push(i);
    } else {
      if (internalPage < 3) {
        pages = [0, 1, 2, "...", totalPagesCount - 1];
      } else if (internalPage > totalPagesCount - 4) {
        pages = [0, "...", totalPagesCount - 3, totalPagesCount - 2, totalPagesCount - 1];
      } else {
        pages = [0, "...", internalPage - 1, internalPage, internalPage + 1, "...", totalPagesCount - 1];
      }
    }

    return pages.map((item, index) => {
      if (item === "...") {
        return (
          <span key={index} className={classes.pageDots}>
            ...
          </span>
        );
      }
      return (
        <Button
          key={item}
          className={internalPage === item ? classes.pageButtonActive : classes.pageButton}
          onClick={() => setPage(item + 1)}
        >
          {item + 1}
        </Button>
      );
    });
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.headerContainer}>
        <Box className={classes.headerLeft}>
          <Button
            className={classes.backButton}
            onClick={() => navigate(-1)}
            startIcon={<ArrowBackOutlined />}
          >
            Back
          </Button>
          <Typography className={classes.pageTitle}>User Wallet Logs</Typography>
        </Box>
        <Typography className={classes.breadcrumb}>
          Admin / <span>User Wallet Logs</span>
        </Typography>
      </Box>

      {/* Rewards Overview Card */}
      <Box className={classes.rewardsCard}>
        <Box className={classes.rewardsLeft}>
          <img
            src="/assets/images/page_images/wallet.png"
            alt="Wallet Icon"
            width={48}
            height={48}
          />
          <Box>
            <Typography className={classes.rewardsTitle}>Total Rewards</Typography>
            <Typography className={classes.rewardsAmount}>
              {totalamount.toFixed(2)} {CUSTOM_CONSTANTS.BASE_CURRENCY}
            </Typography>
          </Box>
        </Box>

        {/* Add Rewards Form */}
        <Box className={classes.addRewardsRow}>
          <TextField
            inputProps={{ form: { autoComplete: "off" } }}
            id="creditedAmount"
            placeholder={`Amount (${CUSTOM_CONSTANTS.BASE_CURRENCY})`}
            value={formik?.values?.creditedAmount}
            onChange={(e) => {
              formik.handleChange(e);
              setSelectedAmount(null);
            }}
            error={Boolean(formik?.errors?.creditedAmount && formik?.touched?.creditedAmount)}
            variant="outlined"
            size="small"
            className={classes.inputShape}
          />

          {MONEY_OPTIONS.map((amount) => (
            <Button
              key={amount}
              variant={selectedAmount === amount ? "contained" : "outlined"}
              className={classes.presetBtn}
              style={{
                backgroundColor: selectedAmount === amount ? "#000000" : "#ffffff",
                color: selectedAmount === amount ? "#ffffff" : "#000000",
                borderColor: "#cbd5e1",
              }}
              onClick={() => handlePresetAmountClick(amount)}
            >
              {amount}
            </Button>
          ))}

          <Button variant="contained" className={classes.addBtn} onClick={formik?.handleSubmit}>
            Add Rewards
          </Button>
        </Box>
      </Box>

      {/* Action Row */}
      <Box className={classes.actionRow}>
        {/* Search */}
        <Box className={classes.searchWrapper}>
          <SearchOutlined className={classes.searchIcon} />
          <input
            type="text"
            placeholder="Search wallet logs..."
            className={classes.searchInput}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(1);
            }}
          />
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} className={classes.tableWrapper}>
        <Table>
          <TableHead>
            <TableRow className={classes.tableHeaderRow}>
              <TableCell className={classes.tableHeaderCell}>S.No</TableCell>
              <TableCell className={classes.tableHeaderCell}>Name</TableCell>
              <TableCell className={classes.tableHeaderCell}>Email</TableCell>
              <TableCell className={classes.tableHeaderCell}>Mobile</TableCell>
              <TableCell className={classes.tableHeaderCell}>Mode</TableCell>
              <TableCell className={classes.tableHeaderCell}>Amount</TableCell>
              <TableCell className={classes.tableHeaderCell}>Status</TableCell>
              <TableCell className={classes.tableHeaderCell}>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dataRows.map((rowItem) => (
              <TableRow key={rowItem.sno} className={classes.tableRow}>
                <TableCell className={classes.tableCell}>{rowItem.sno}</TableCell>
                <TableCell className={`${classes.tableCell} ${classes.boldText}`}>
                  {rowItem.username}
                </TableCell>
                <TableCell className={classes.tableCell}>{rowItem.email}</TableCell>
                <TableCell className={classes.tableCell}>{rowItem.mobile}</TableCell>
                <TableCell className={classes.tableCell}>Added by ({rowItem.mode})</TableCell>
                <TableCell className={classes.tableCell}>
                  {rowItem.amount} {CUSTOM_CONSTANTS.BASE_CURRENCY}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Box
                    sx={{
                      display: "inline-block",
                      padding: "4px 12px",
                      borderRadius: "100px",
                      fontSize: "12px",
                      fontWeight: "600",
                      textTransform: "capitalize",
                      backgroundColor: "#e0f2fe",
                      color: "#0369a1",
                    }}
                  >
                    {rowItem.status}
                  </Box>
                </TableCell>
                <TableCell className={classes.tableCell}>{rowItem.createdAt}</TableCell>
              </TableRow>
            ))}
            {dataRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} style={{ textAlign: "center", padding: "40px" }}>
                  <Typography variant="body2" color="textSecondary">
                    No wallet logs found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Box className={classes.paginationContainer}>
          <Typography className={classes.paginationText}>
            Showing {(page - 1) * pageSize + (dataRows.length > 0 ? 1 : 0)} to{" "}
            {Math.min(page * pageSize, totalPages)} of {totalPages} results
          </Typography>

          <Box className={classes.paginationControls}>
            <ChevronLeft
              className={`${classes.pageArrow} ${page === 1 ? "disabled" : ""}`}
              onClick={() => page > 1 && setPage(page - 1)}
            />
            {renderPagination()}
            <ChevronRight
              className={`${classes.pageArrow} ${page === totalPagesCount ? "disabled" : ""}`}
              onClick={() => page < totalPagesCount && setPage(page + 1)}
            />
          </Box>
        </Box>
      </TableContainer>
    </Box>
  );
};

export default Userwalletlogs;
